import { useState, useEffect, useRef } from "react";
import { API_HOST } from "../constants";
import type { Task } from "../types";

// Глобальный кеш для задач
let tasksCache: Task[] | null = null; // Кеш для базового списка
let tasksFullCache: Task[] | null = null; // Кеш для полных деталей
let tasksCachePromise: Promise<Task[]> | null = null;
let tasksFullCachePromise: Promise<Task[]> | null = null;
let cacheTimestamp: number = 0;
let fullCacheTimestamp: number = 0;
let isFetching = false; // Флаг для предотвращения дублирующихся запросов
const CACHE_DURATION = 5 * 60 * 1000; // 5 минут

interface UseTasksOptions {
  fetchFullDetails?: boolean; // Загружать ли полную информацию о каждой задаче
  skipCache?: boolean; // Пропустить кеш
}

export const useTasks = (options: UseTasksOptions = {}) => {
  const { fetchFullDetails = false, skipCache = false } = options;
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    // Устанавливаем флаг монтирования
    isMountedRef.current = true;

    const fetchTasks = async () => {
      try {
        // Проверяем кеш ПЕРЕД установкой loading, чтобы избежать лишних ре-рендеров
        const now = Date.now();
        
        if (fetchFullDetails) {
          // Проверяем кеш для полных деталей
          if (
            !skipCache &&
            tasksFullCache &&
            (now - fullCacheTimestamp) < CACHE_DURATION
          ) {
            if (isMountedRef.current) {
              setTasks(tasksFullCache);
              setLoading(false);
            }
            return;
          }
          // Проверяем промис для полных деталей
          if (tasksFullCachePromise && !skipCache) {
            try {
              const cachedTasks = await tasksFullCachePromise;
              if (isMountedRef.current) {
                setTasks(cachedTasks);
                setLoading(false);
              }
              return;
            } catch {
              tasksFullCachePromise = null;
              isFetching = false;
            }
          }
        } else {
          // Проверяем кеш для базового списка
          if (
            !skipCache &&
            tasksCache &&
            (now - cacheTimestamp) < CACHE_DURATION
          ) {
            if (isMountedRef.current) {
              setTasks(tasksCache);
              setLoading(false);
            }
            return;
          }
          // Проверяем промис для базового списка
          if (tasksCachePromise && !skipCache) {
            try {
              const cachedTasks = await tasksCachePromise;
              if (isMountedRef.current) {
                setTasks(cachedTasks);
                setLoading(false);
              }
              return;
            } catch {
              tasksCachePromise = null;
              isFetching = false;
            }
          }
        }

        // Устанавливаем флаг загрузки только если действительно делаем запрос
        setLoading(true);
        setError(null);

        // Создаем промис СНАЧАЛА (синхронно) - это ключевой момент для защиты от дублирования
        // Если нужны полные детали, используем параметр full=true для получения всех данных одним запросом
        const url = fetchFullDetails 
          ? `${API_HOST}/api/tasks?full=true`
          : `${API_HOST}/api/tasks`;
        
        const fetchPromise = (async () => {
          const res = await fetch(url);

          if (!res.ok) {
            throw new Error("Ошибка при загрузке задач");
          }

          const data = await res.json();

          // Если использовали full=true, данные уже содержат все детали
          // Если нет - возвращаем как есть (превью)
          return data;
        })();

        // Сохраняем промис в соответствующий кеш СИНХРОННО ПЕРЕД await
        if (fetchFullDetails) {
          tasksFullCachePromise = fetchPromise;
          isFetching = true;
        } else {
          tasksCachePromise = fetchPromise;
          isFetching = true;
        }

        const fetchedTasks = await fetchPromise;

        // Проверяем, не был ли компонент размонтирован
        if (!isMountedRef.current) {
          // Компонент размонтирован, но все равно обновляем кеш для других компонентов
          if (fetchFullDetails) {
            tasksFullCache = fetchedTasks;
            fullCacheTimestamp = now;
            tasksFullCachePromise = null;
            isFetching = false;
          } else {
            tasksCache = fetchedTasks;
            cacheTimestamp = now;
            tasksCachePromise = null;
            isFetching = false;
          }
          return;
        }

        // Обновляем соответствующий кеш
        if (fetchFullDetails) {
          tasksFullCache = fetchedTasks;
          fullCacheTimestamp = now;
          tasksFullCachePromise = null;
          isFetching = false;
        } else {
          tasksCache = fetchedTasks;
          cacheTimestamp = now;
          tasksCachePromise = null;
          isFetching = false;
        }

        // Обновляем состояние только если компонент все еще смонтирован
        setTasks(fetchedTasks);
      } catch (err) {
        // Сбрасываем флаг при ошибке
        if (fetchFullDetails) {
          isFetching = false;
          tasksFullCachePromise = null;
        } else {
          isFetching = false;
          tasksCachePromise = null;
        }

        console.error("Error fetching tasks:", err);
        if (isMountedRef.current) {
          setError(err instanceof Error ? err : new Error("Неизвестная ошибка"));
        }
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    };

    fetchTasks();

    // Очистка при размонтировании
    // НЕ отменяем запрос - это создает cancelled запросы в Network tab
    return () => {
      isMountedRef.current = false;
    };
  }, [fetchFullDetails, skipCache]);

  // Функция для принудительного обновления кеша
  const refetch = () => {
    if (fetchFullDetails) {
      tasksFullCache = null;
      tasksFullCachePromise = null;
      fullCacheTimestamp = 0;
    } else {
      tasksCache = null;
      tasksCachePromise = null;
      cacheTimestamp = 0;
    }
    isFetching = false;
    setLoading(true);
  };

  return { tasks, loading, error, refetch };
};


